import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, Field } from "@/pages/Login";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { nicheByKey } from "@/lib/niches";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim();
  const pickedNiche = nicheByKey((searchParams.get("niche") || "").trim());

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accepted) {
      setError("Please accept the Content & Copyright Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, accepted_policy: accepted, referral_code: refCode || null, signup_niche: pickedNiche?.v || null });
      loginWithData(data);
      navigate(pickedNiche ? `/create?niche=${pickedNiche.v}` : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your studio" subtitle="Free trial — make a couple of short videos free">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="register-error">{error}</p>}
        {pickedNiche && (
          <p className="text-sm text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-md px-3 py-2" data-testid="register-niche-note">
            🎬 Nice pick — after signup we'll set up your first <span className="font-semibold">{pickedNiche.label}</span> video.
          </p>
        )}
        {refCode && (
          <p className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2" data-testid="register-referral-note">
            🎁 You were invited by a friend — subscribe to a plan and you both win!
          </p>
        )}
        <Field label="Name" value={name} onChange={setName} testid="register-name" />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="register-email" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="register-password" />
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            data-testid="register-policy-checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-white/20 bg-[#0A0A0A] accent-blue-600 cursor-pointer"
          />
          <span className="text-xs text-zinc-400 leading-relaxed">
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="text-blue-400 hover:text-blue-300 underline">Terms of Service</Link>,{" "}
            <Link to="/privacy" target="_blank" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link>, and{" "}
            <Link to="/legal" target="_blank" className="text-blue-400 hover:text-blue-300 underline">Content &amp; Copyright Policy</Link>, and won't publish copyrighted material I don't own.
          </span>
        </label>
        <button disabled={loading || !accepted} data-testid="register-submit" className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed py-2.5 rounded-md font-semibold transition-colors">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-zinc-600 uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <GoogleSignInButton label="Sign up with Google" />
      <p className="text-[11px] text-zinc-600 mt-2 text-center">
        By continuing with Google you agree to the Terms, Privacy and Content &amp; Copyright Policy.
      </p>
      <p className="text-sm text-zinc-500 mt-6 text-center">
        Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300">Sign in</Link>
      </p>
    </AuthShell>
  );
}
